import { useState, useEffect, useRef } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { capitalize } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { singleProductActions, singleProductSelectors } from '../../../store/single-product';
import { RootState } from '../../../store/store';
import { ReactComponent as RemoveItem } from '../../../svg/removeItem.svg';
import { ReactComponent as RemoveItemSelected } from '../../../svg/removeItemSelected.svg';
import { Modifier } from '../../../types';
import {
  ListDataValue,
  RemoveItemButton,
  RemoveItemContainer,
  RemoveItemIcon,
  SectionDivider,
  StyledAccordion,
  StyledAccordionSummary,
  TypographyStyled,
  StyledAccordionDetails
} from './general-panel.styles';
import { ModifiersAutocomplete } from './modifiers-auto-complete';
import { StyledModifiersList, StyledModifiersListItem } from './modifiers-panel.styles';
import { PanelProps } from './panel-types';

export const MODIFIERS_CAP: string = 'Modifiers';
export const MODIFIERS_LOW: string = 'modifiers';
export function modifierTextToShow(modifier: Modifier): string {
  return `${capitalize(modifier.type)} - ${capitalize(modifier.name)}`;
}

export function ModifiersPanel(props: PanelProps): JSX.Element {
  const currentModifiers = useSelector((state: RootState) =>
    singleProductSelectors.selectProductModifiers(state.singleProduct)
  );

  const pricelistModifiers = useSelector((state: RootState) =>
    singleProductSelectors.selectPricelistModifiers(state.singleProduct)
  );

  const bottomRef = useRef<HTMLHRElement | null>(null);
  const [addedModifiers, setAddedModifiers] = useState<boolean>(false);

  function modifiersToArray() {
    return currentModifiers ? currentModifiers.map((modifier) => modifierTextToShow(modifier)) : [];
  }

  const modifiers = modifiersToArray();

  const dispatch = useDispatch();

  const modifiersComboBox = createArrayModifiersComboBox(pricelistModifiers, currentModifiers);

  function createArrayModifiersComboBox(
    modifiers: Modifier[],
    currentModifiers: Modifier[]
  ): Modifier[] {
    let modifiersComboBoxArray: Modifier[] = [];
    let currentModifiersIds = currentModifiers.map((modifier) => modifier.id);
    modifiersComboBoxArray = modifiers.filter(
      (modifier) => !currentModifiersIds.includes(modifier.id)
    );
    return modifiersComboBoxArray;
  }

  function handleDeleteModifiers(value: string) {
    props.setIsChange && props.setIsChange(true);
    let deletedModifier = pricelistModifiers.find(
      (modifier) => modifierTextToShow(modifier) === value
    );
    let newModifiersArray = currentModifiers.filter(
      (modifier) => modifier.id !== deletedModifier?.id
    );

    dispatch(
      singleProductActions.updateProduct({
        fieldName: 'modifiers',
        value: newModifiersArray
      })
    );
  }

  function handleAddModifiers(value: string) {
    props.setIsChange && props.setIsChange(true);

    let newModifier = modifiersComboBox.find((modifier) => modifierTextToShow(modifier) === value);
    let newModifierList = [...currentModifiers];
    if (newModifier) {
      newModifierList.push(newModifier);
    }
    dispatch(
      singleProductActions.updateProduct({
        fieldName: 'modifiers',
        value: newModifierList
      })
    );
    setAddedModifiers(true);
  }

  useEffect(() => {
    if (addedModifiers) {
      bottomRef?.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
    setAddedModifiers(false);
    // eslint-disable-next-line
  }, [modifiers]);

  return (
    <div>
      <StyledAccordion
        id={`${MODIFIERS_LOW}-accordion`}
        expanded={props.expanded === MODIFIERS_CAP}
        onChange={props.handleAccordionChange(MODIFIERS_CAP, modifiers.length)}
        disableGutters
        elevation={0}
        TransitionProps={{
          onEntered: (node: HTMLElement) => {
            bottomRef?.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
          }
        }}
      >
        <StyledAccordionSummary
          expandIcon={(props.allowEditing || modifiers.length > 0) && <ExpandMoreIcon id={`${MODIFIERS_LOW}-expand-more-icon`}/>}
          aria-controls={`${MODIFIERS_LOW}-content`}
          id={`${MODIFIERS_LOW}-header`}
        >
          <TypographyStyled>
            {props.expanded === MODIFIERS_CAP
              ? MODIFIERS_CAP
              : `${MODIFIERS_CAP} (${modifiers.length})`}
          </TypographyStyled>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          {props.allowEditing && modifiersComboBox && (
            <ModifiersAutocomplete
              values={modifiersComboBox}
              valueAddedHandler={handleAddModifiers}
            />
          )}
          {modifiers.length > 0 && (
            <StyledModifiersList id={`${MODIFIERS_LOW}-list`} disablePadding dense>
              {modifiers.map((value, index) => (
                <StyledModifiersListItem key={value} index={index} className='textList'>
                  <ListDataValue id={`${value}-data-value`}>{value}</ListDataValue>
                  {props.allowEditing && (
                    <RemoveItemContainer className='removeItem'>
                      <RemoveItemIcon id={`${value}-remove-icon`}>
                        <RemoveItem />
                      </RemoveItemIcon>
                      <RemoveItemButton
                        id={`${value}-remove-button`}
                        onClick={() => {
                          handleDeleteModifiers(value);
                        }}
                      >
                        <RemoveItemSelected />
                      </RemoveItemButton>
                    </RemoveItemContainer>
                  )}
                </StyledModifiersListItem>
              ))}
            </StyledModifiersList>
          )}
        </StyledAccordionDetails>
      </StyledAccordion>
      <SectionDivider id={`${MODIFIERS_LOW}-divider`} ref={bottomRef} />
    </div>
  );
}
